import { motion } from "framer-motion";
import { DollarSign, ChevronDown, Calendar, Clock } from "lucide-react";
import { formatRevenue, formatGoalDate, countdownColor, countdownLabel } from "./revenueModel";

const FLOAT = { y: [0, -8, 0], rotate: [0, 5, -5, 0] };
const FLOAT_LOOP = { duration: 4, repeat: Infinity, ease: "easeInOut" };
const FADE_IN = { opacity: 1 };
const FADE_OUT = { opacity: 0 };
const FADE_DELAY = { delay: 0.2 };
const CHEVRON_OPEN = { rotate: 180 };
const CHEVRON_CLOSED = { rotate: 0 };
const CHEVRON_SPIN = { duration: 0.3 };

const Countdown = ({ goalEndDate, daysRemaining }) => (
  <motion.div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:ml-14" initial={FADE_OUT} animate={FADE_IN} transition={FADE_DELAY}>
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
      <Calendar className="h-3.5 w-3.5 text-gray-400" />
      <span className="text-xs text-gray-300">Target: {formatGoalDate(goalEndDate)}</span>
    </div>
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${daysRemaining <= 30 ? "bg-red-500/10 border border-red-500/20" : "bg-ism-teal/10 border border-ism-teal/20"}`}>
      <Clock className={`h-3.5 w-3.5 ${countdownColor(daysRemaining)}`} />
      <span className={`text-xs font-semibold ${countdownColor(daysRemaining)}`} data-testid="days-remaining">{countdownLabel(daysRemaining)}</span>
    </div>
  </motion.div>
);

// Goal figure + countdown on the left, progress % beside it (stacked under the goal on phones), expand chevron on the right.
export const RevenueHeader = ({ goal, progressPercentage, goalEndDate, daysRemaining, instanceName, isExpanded }) => (
  <div className={`flex items-start justify-between gap-3 transition-all duration-500 ${isExpanded ? "mb-8" : "mb-0"}`}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 min-w-0 flex-1">
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <motion.div className="float-element flex-shrink-0" animate={FLOAT} transition={FLOAT_LOOP}>
            <div className="p-3 rounded-xl bg-gradient-to-br from-ism-teal/20 to-ism-teal/5 border border-ism-teal/20"><DollarSign className="h-7 w-7 sm:h-8 sm:w-8 text-ism-teal" /></div>
          </motion.div>
          <div className="min-w-0">
            <span className="block text-4xl sm:text-5xl text-ism-teal tracking-tight data-value truncate" data-testid="revenue-goal-value">{formatRevenue(goal)}</span>
            <p className="text-xs text-gray-500 tracking-wider mt-1 truncate">{instanceName ? `${instanceName} Goal` : "Revenue Goal"}</p>
          </div>
        </div>
        {goalEndDate && <Countdown goalEndDate={goalEndDate} daysRemaining={daysRemaining} />}
      </div>
      <div className="flex items-center gap-4">
        <div className="hidden sm:block h-12 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent" />
        <div>
          <p className="text-xs text-gray-500 tracking-wider">Progress</p>
          <p className="text-3xl text-ism-teal data-value" data-testid="revenue-progress-pct">{Math.round(progressPercentage)}%</p>
        </div>
      </div>
    </div>
    <motion.div animate={isExpanded ? CHEVRON_OPEN : CHEVRON_CLOSED} transition={CHEVRON_SPIN} className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex-shrink-0">
      <ChevronDown className="h-5 w-5 text-gray-400" />
    </motion.div>
  </div>
);
